import { makeAccess } from '../config/factory-controller'

interface CollectionOrderResponse {
  CollectionOrderId: number
  Status: number
  BankName: string
  PrincipalValue: number
  Token: string
  PayerPhone?: string
  CreationDate?: string
}

interface PaginationInfo {
  currentPage: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

interface PagedResponse<T> {
  data: T[]
  pagination: PaginationInfo
}

export const collectionOrderService = {
  async getCollectionOrdersPaged(
    pageIndex: number = 0,
    pageSize: number = 10,
    status?: number,
    startDate?: string,
    endDate?: string,
    filterToken?: string,
  ): Promise<PagedResponse<CollectionOrderResponse>> {
    try {
      const API_URL =
        import.meta.env.VITE_REACT_APP_API_URL || 'https://localhost:50962'
      const cleanApiUrl = API_URL.replace(/\/+$/, '')

      // Headers centralizados
      const { key: applicationKey, token: applicationToken } =
        await makeAccess()
      const apiVersion = Number(import.meta.env.VITE_REACT_APP_API_VERSION)

      if (!applicationKey || !applicationToken) {
        throw new Error('Credenciales de aplicación faltantes (key/token).')
      }

      // Construindo a URL com os parâmetros necessários
      const params = new URLSearchParams({
        pageIndex: pageIndex.toString(),
        pageSize: pageSize.toString(),
      })

      // Somente adiciona status se definido (aceita 0)
      if (status !== undefined) params.append('status', status.toString())
      if (startDate) params.append('initialDate', startDate)
      if (endDate) params.append('finalDate', endDate)
      if (filterToken) params.append('token', filterToken)

      const url = `${cleanApiUrl}/collectionorder/getcollectionorderlist?${params.toString()}`

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-api-version': String(apiVersion),
        'x-application-key': applicationKey,
        'x-application-token': applicationToken,
      }

      const bearer = localStorage.getItem('token')
      if (bearer) {
        headers['Authorization'] = `Bearer ${bearer}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()

      const apiData = result?.data || {}
      const items = apiData.items || []

      // Mapeia para o formato esperado pelo slice (PascalCase nas chaves)
      const normalizedItems: CollectionOrderResponse[] = items.map(
        (it: any) => ({
          CollectionOrderId: it.collectionOrderId ?? it.CollectionOrderId ?? 0,
          Status: it.status ?? it.Status ?? 0,
          BankName: it.bankName ?? it.BankName ?? '',
          PrincipalValue: it.principalValue ?? it.PrincipalValue ?? 0,
          Token: it.token ?? it.Token ?? '',
          PayerPhone: it.payerPhone ?? it.PayerPhone,
          CreationDate: it.creationDate ?? it.CreationDate,
        }),
      )

      const pagination: PaginationInfo = {
        currentPage: (apiData.currentPage ?? pageIndex) + 1,
        pageSize: apiData.pageSize ?? pageSize,
        totalItems: apiData.totalItems ?? normalizedItems.length,
        totalPages: apiData.totalPages ?? 1,
        hasPreviousPage:
          apiData.hasPreviousPage ?? (apiData.currentPage ?? pageIndex) > 0,
        hasNextPage:
          apiData.hasNextPage ??
          (apiData.currentPage ?? pageIndex) + 1 < (apiData.totalPages ?? 1),
      }

      return { data: normalizedItems, pagination }
    } catch (error) {
      // Log pode ser tratado por monitoramento externo
      throw error
    }
  },
}
